import React from "react";
import { Document, Page, View, Text, Image } from "@react-pdf/renderer";
import { styles, statusColour } from "./pdfTheme";
import { thumbFor } from "./photos";
import {
  Cover, CoverDetails, Chrome, Heading, Narrative, ScoreAnalysis, OverallAssessment, Scope,
} from "./pdfSections";

/**
 * The Standard layout — the client report as a register.
 *
 * One row per finding in a landscape table, matching the existing HSE layout
 * the auditors already know, at roughly seven findings per page (design §7a).
 * Elaborate — one entry per finding, set out in full — is CsaElaboratePdf.js,
 * drawing the same model.
 *
 * Everything except section 7 is shared between the two layouts and lives in
 * pdfSections.js. Only the findings differ, which is what stops a client
 * finding a different percentage in two renderings of the same audit.
 *
 * This renderer makes no decisions. Everything it draws was already chosen by
 * csaReportComposer on the server: which findings appear, what each one says,
 * what is redacted. If something here needs an `if` about *whether* to print,
 * that logic is in the wrong place.
 */



/* ------------------------------------------------------------------ */
/* 7 — the register                                                    */
/* ------------------------------------------------------------------ */

function Findings({ section, header }) {
  const d = section.data;
  const cols = d.columns;
  const w = (key) => {
    const c = cols.find((x) => x.key === key);
    return { width: `${c ? c.width : 10}%` };
  };

  if (!d.rows.length) {
    return (
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Chrome header={header} />
        <Heading no={section.no} title={section.title} />
        <Text style={styles.empty}>No findings were selected for this report.</Text>
      </Page>
    );
  }

  return (
    <Page size="A4" orientation="landscape" style={styles.page}>
      <Chrome header={header} />
      <Heading no={section.no} title={section.title} />
      <Text style={[styles.muted, { marginBottom: 8 }]}>
        {d.selected} finding{d.selected === 1 ? "" : "s"} shown, from {d.assessed} questions
        assessed.
      </Text>

      {/* `fixed` repeats the header on every page the table spills onto. */}
      <View style={styles.regHead} fixed>
        {cols.map((c) => (
          <Text key={c.key} style={[styles.regHeadCell, w(c.key)]}>
            {c.label.toUpperCase()}
          </Text>
        ))}
      </View>

      {d.rows.map((r, i) => (
        <View key={r.number} style={[styles.regRow, i % 2 ? styles.zebra : {}]} wrap={false}>
          <Text style={[styles.regCell, w("sr_no")]}>{r.sr_no}</Text>
          <Text style={[styles.regCell, w("pillar")]}>{r.pillar}</Text>
          <Text style={[styles.regCell, w("sub_pillar")]}>{r.sub_pillar}</Text>
          <Text style={[styles.regCell, w("verification_method")]}>
            {r.verification_method || "—"}
          </Text>

          <View style={[styles.regCell, w("status")]}>
            <Text style={[styles.statusPill, { backgroundColor: statusColour(r.status) }]}>
              {r.status}
            </Text>
          </View>

          <Text style={[styles.regCell, w("observation")]}>{r.observation}</Text>
          <Text style={[styles.regCell, w("recommendation")]}>{r.recommendation || "—"}</Text>
          <Text style={[styles.regCell, w("legal_reference")]}>{r.legal_reference || "—"}</Text>
          <Text style={[styles.regCell, w("score")]}>
            {r.score === null ? "—" : `${r.score}/${r.max_pts}`}
          </Text>

          <View style={[styles.regCell, w("photos")]}>
            {r.photos && r.photos.length ? (
              <View style={styles.thumbRow}>
                {r.photos.slice(0, 4).map((u, k) => (
                  <Image key={k} src={thumbFor(u)} style={styles.thumb} />
                ))}
              </View>
            ) : (
              <Text>—</Text>
            )}
          </View>
        </View>
      ))}
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* the document                                                        */
/* ------------------------------------------------------------------ */

export default function CsaStandardPdf({ model }) {
  const { header, summary, sections } = model;
  const byKey = (k) => sections.find((s) => s.key === k);

  const renderSection = (s) => {
    if (s.key === "score_analysis") return <ScoreAnalysis key={s.key} section={s} />;
    if (s.key === "overall_assessment") return <OverallAssessment key={s.key} section={s} />;
    if (s.key === "scope") return <Scope key={s.key} section={s} />;
    return <Narrative key={s.key} section={s} />;
  };

  // Portrait pages, in document order, with the register lifted out because it
  // is landscape — ten columns will not fit otherwise.
  const before = sections.filter((s) => s.no < 7);
  const after = sections.filter((s) => s.no > 7);

  return (
    <Document
      title={`CSA — ${header.organization} — ${header.site}`}
      author={header.prepared_by || "Momentum"}
      subject="Critical Safety Assessment"
    >
      <Cover header={header} summary={summary} />

      {/* What the cover used to carry. The branded frame has room for four
          lines and a title, so these moved rather than went. */}
      <Page size="A4" style={styles.page}>
        <Chrome header={header} />
        <CoverDetails header={header} summary={summary} />
      </Page>

      {before.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}

      <Findings section={byKey("findings")} header={header} />

      {after.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}
    </Document>
  );
}
